import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { MessagesSquare, ListPlus, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = { closed: "bg-slate-100 text-slate-600", open: "bg-emerald-100 text-emerald-700" };
const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const staffNames = (t) => t.participants.filter((p) => p.kind === "staff").map((p) => p.name).filter(Boolean);

export function ChatStatusChip({ t }) {
  return <span className={`text-xs rounded-full px-2 py-0.5 shrink-0 ${STATUS_STYLE[t.is_open ? "open" : "closed"]}`} data-testid={`chat-status-${t.id}`}>{t.status_label}</span>;
}

export function ChatHistoryRow({ t, onOpen }) {
  return (
    <div className="px-4 py-3 flex gap-3 hover:bg-slate-50/70 transition-colors" data-testid={`chat-row-${t.id}`}>
      <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-[#0D9488]/10 text-[#0D9488] flex items-center justify-center"><MessagesSquare className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex justify-between gap-2">
          <span className="font-medium text-slate-800 text-sm">Support Chat · {t.person_name}<span className="text-slate-400 font-normal"> {t.human_id}</span></span>
          <ChatStatusChip t={t} />
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{[t.organization_name, `Started ${fmt(t.opened_at)}`, t.closed_at && `Closed ${fmt(t.closed_at)}`].filter(Boolean).join(" · ")}</p>
        <p className="text-xs text-slate-400">Participants: {[t.person_name, ...staffNames(t)].join(", ")} · {t.message_count} message{t.message_count === 1 ? "" : "s"}</p>
        {t.preview && <p className="text-xs text-slate-600 mt-1 line-clamp-1 italic">"{t.preview}"</p>}
        <button onClick={() => onOpen(t.id)} className="mt-1 text-xs text-brand font-medium hover:underline" data-testid={`chat-open-${t.id}`}>Open Thread →</button>
      </div>
    </div>
  );
}

export function ChatThreadDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  const [task, setTask] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get(`/support/chat-threads/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = d?.thread;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="chat-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessagesSquare className="w-5 h-5 text-[#0D9488]" />Support Chat {t?.human_id || ""}</DialogTitle>
          <DialogDescription>{t ? `${t.person_name} · ${t.organization_name || "No Group"}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {t && <>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="chat-thread-meta">
            <Meta label="Person"><button className="text-brand hover:underline" onClick={() => navigate(`/people/detail/${t.person_id}`)} data-testid="chat-thread-person">{t.person_name}</button></Meta>
            <Meta label="Group">{t.organization_name || "—"}</Meta>
            <Meta label="Status"><ChatStatusChip t={t} /></Meta>
            <Meta label="Assigned">{t.assigned_support_name || "Unassigned"}</Meta>
            <Meta label="Started">{fmt(t.opened_at)}</Meta>
            <Meta label="Closed">{t.closed_at ? fmt(t.closed_at) : "—"}</Meta>
            <div className="sm:col-span-2 flex items-start gap-2 pt-1"><Users className="w-4 h-4 text-slate-400 mt-0.5" /><span className="text-slate-700" data-testid="chat-thread-participants">{[t.person_name, ...staffNames(t)].join(", ")}</span></div>
          </div>
          <div className="max-h-[45vh] overflow-auto space-y-2 py-1 pr-1" data-testid="chat-thread-messages">
            {d.messages.map((m) => m.sender_type === "system"
              ? <p key={m.id} className="text-center text-[11px] text-slate-400">{m.message_body} · {fmt(m.created_at)}</p>
              : <div key={m.id} className={`flex ${m.sender_type === "support_staff" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender_type === "support_staff" ? "bg-brand text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                    <p className={`text-[10px] font-semibold mb-0.5 ${m.sender_type === "support_staff" ? "text-white/70" : "text-slate-500"}`}>{m.sender_type === "support_staff" ? m.sender_name : t.person_name} · {fmt(m.created_at)}</p>
                    <p className="whitespace-pre-wrap">{m.message_body}</p>
                  </div>
                </div>)}
            {!d.messages.length && <p className="text-sm text-slate-400 text-center py-6">No messages in this thread.</p>}
          </div>
          {d.tasks.length > 0 && <div className="text-xs text-slate-600" data-testid="chat-thread-tasks"><span className="font-semibold">Follow-up tasks:</span> {d.tasks.map((x) => <button key={x.id} onClick={() => navigate(`/tasks/detail/${x.id}`)} className="ml-2 text-brand hover:underline">{x.human_id} {x.title}</button>)}</div>}
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {t.is_open && <Button variant="outline" size="sm" onClick={() => navigate(`/communications?tab=support&conv=${t.id}`)} data-testid="chat-thread-open-live"><ExternalLink className="w-4 h-4 mr-1" />Open in Support</Button>}
            <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => setTask(true)} data-testid="chat-thread-create-task"><ListPlus className="w-4 h-4 mr-1" />Create Follow-up Task</Button>
          </div>
        </>}
        {task && <TaskFormDialog open onClose={() => setTask(false)} onCreated={load} title="Create Follow-up Task"
          defaults={{ title: `Follow up: ${t.person_name} support chat`, person_id: t.person_id || "", description: `From Support Chat ${t.human_id || ""}`, support_conversation_id: t.id }} />}
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, children }) {
  return <div className="flex items-center gap-2"><span className="text-slate-400 w-16 shrink-0 text-xs">{label}</span><span className="text-slate-800 min-w-0">{children}</span></div>;
}
